import React from "react";
import { useTranslation } from "react-i18next";

import RedeemList from "./RedeemList";

/**
 * "Who is in the room" — the two kinds of guest a till has to serve, in one list.
 *
 *  • **Ordered today.** The ordinary case; order-first made the identity capture
 *    at order time BE the check-in.
 *  • **🎁 At the counter.** P5b. Someone who opened their card at home, saw a
 *    reward was unlocked and walked in for it having ordered NOTHING. They are
 *    standing at the till and a list built from orders cannot see them at all.
 *    Their tap puts them here, pinned to the top with a pulsing highlight.
 *
 * The highlight is loud on purpose and it is the only thing on this board that
 * pulses. A guest who has announced themselves and is being ignored is worse
 * than one who never announced: the feature teaches them to expect it to work.
 *
 * A guest can be both — ordered AND tapped. They appear once, flagged.
 *
 * Redemption itself lives in RedeemList, shared with the search panel, so the
 * board and the fallback can never drift apart on the thing that matters (the
 * per-redemption idempotency key, F-16).
 */
export default function GuestStrip({ token, checkins, rewards, currencySymbol, onPickForPoints, onRedeemed }) {
  const { t } = useTranslation();
  const [openId, setOpenId] = React.useState(null);

  if (!checkins?.length) return null;

  const waiting = checkins.filter((g) => g.at_counter).length;

  return (
    <div className="cds-guests">
      <div className="cds-guests-head">
        <span className="ic" aria-hidden="true">👥</span>
        <div>
          <div className="ttl">{t("cashier.guests.title")}</div>
          <div className="sub">
            {waiting > 0 ? t("cashier.guests.waiting", { count: waiting }) : t("cashier.guests.sub")}
          </div>
        </div>
      </div>

      <div className="cds-guest-list">
        {checkins.map((g) => {
          // A waiting guest is ALWAYS expanded. Their entire purpose is "act on
          // me now", and putting the Redeem button behind a tap on the one row
          // that is already asking for attention is exactly the friction this
          // feature exists to remove. Everyone else collapses as before.
          const open = g.at_counter || openId === g.id;
          const ready = g.can_redeem ? (rewards || []).filter((r) => g.balance >= r.cost) : [];

          return (
            <div
              key={g.id}
              className={`cds-guest${open ? " open" : ""}${g.at_counter ? " here" : ""}`}
            >
              <button type="button" className="cds-guest-row"
                onClick={() => setOpenId(open ? null : g.id)} aria-expanded={open}>
                <span className="av" aria-hidden="true">
                  {g.at_counter ? "🎁" : (g.name || "?").trim().charAt(0).toUpperCase()}
                </span>
                <span className="who">
                  <span className="nm">
                    {g.name || t("cashier.pts.no_name")}
                    {g.at_counter && (
                      <span className="cds-here-tag">{t("cashier.guests.at_counter")}</span>
                    )}
                  </span>
                  <span className="mt">
                    {g.at_counter && <span className="here-line">{t("cashier.guests.here_for_reward")}</span>}
                    {g.last_order?.no && <span className="mono">#{g.last_order.no}</span>}
                    {g.table_number && <span>· {t("cashier.table")} {g.table_number}</span>}
                    {g.last_order && !g.last_order.paid && (
                      <span className="unpaid">· {t("cashier.guests.unpaid")}</span>
                    )}
                  </span>
                </span>
                <span className="bal">
                  <span className="n mono">{g.balance}</span>
                  <span className="l">{t("cashier.guests.points")}</span>
                </span>
                {ready.length > 0 && <span className="cds-guest-flag">{ready.length}</span>}
              </button>

              {open && (
                <div className="cds-guest-body">
                  <RedeemList
                    token={token}
                    guest={g}
                    rewards={rewards}
                    wantsRewardId={g.wants_reward_id}
                    onRedeemed={onRedeemed}
                  />

                  <button type="button" className="cds-guest-pts"
                    onClick={() => onPickForPoints?.(g)}>
                    ➕ {t("cashier.guests.add_points", { name: g.name || "" })}
                  </button>

                  {/* An unpaid QR ticket means Mark paid is about to credit the
                      points for THAT money. Adding the whole bill on top of it
                      pays twice for the same coffee — the cashier is told
                      before they tap, not after. */}
                  {g.last_order && !g.last_order.paid && g.last_order.total > 0 && (
                    <div className="cds-guest-warn">
                      ⚠️ {t("cashier.guests.qr_open", {
                        no: g.last_order.no,
                        amount: Number(g.last_order.total).toFixed(2),
                        currency: currencySymbol,
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
